import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, take, tap } from 'rxjs';
import
  {
    Address,
    ApplicantClient,
    ApplicantDetailsDto,
    ApplicantProfileDto,
    ContactData,
    FullName,
    JobApplicationClient,
    PassportData,
    JobApplicationDto,
    JobApplicationFileDto,
    JobApplicationFilesClient,
    JobApplicationMyDto,
    JobApplicationResultClient,
    JobApplicationResultDto,
    MIPIntegration,
    ProfileAddressDto,
    ProfileContactDto,
    VacancyClient,
    VacancyDetailsDto,
    JobApplicationStatus,
    SelectedApplicationStatus,
  } from 'src/NSwag/nswag-api-pimmaJob';
import { SpService } from 'src/app/core/services/sp.service';
import { UserService } from 'src/app/core/user/user.service';
import { DocumentsComponent } from './profile/add-update/documents/documents.component';
import { environment } from 'src/environment/environment';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import
  {
    EmployeeCurrentWorkDataModel,
    EmployeeExperiences,
    EmployeePosition,
    EmployeeWorkDataModel,
    LaborApiRequest,
    MIPClient,
    PedCategoryDto,
    PedCategoryRequestAPI,
    PersonDiplomApi,
    RequestCadastr,
    RequestDiploma,
  } from 'src/NSwag/nswag-api-mip';

@Injectable({
  providedIn: 'root',
})
export class ApplicantService
{
  constructor(
    public translocoService: TranslocoService,
    public applicantClient: ApplicantClient,
    public jobApplicantClient: JobApplicationClient,
    public jobApplicationFilesClient: JobApplicationFilesClient,
    public vacancyClient: VacancyClient,
    public spService: SpService,
    public dialogService: DialogService,
    private _httpClient: HttpClient,
    public messageService: MessageService,
    private mipClient: MIPClient,
    private uService: UserService,
    private jobApplicationResultClient: JobApplicationResultClient,
  ) { }

  applicant: ApplicantProfileDto | ApplicantDetailsDto = new ApplicantProfileDto();
  applicant$ = new BehaviorSubject<ApplicantProfileDto | ApplicantDetailsDto | null>(null);
  readonly applicantStream$ = this.applicant$.asObservable();
  applications: JobApplicationDto[] = [];
  pipe = new DatePipe('en-US');
  ref: DynamicDialogRef;
  private readonly _baseUrl = environment.API_BASE_URL;
  applicantDiplomaInfo: PersonDiplomApi[] = [];
  employeeWorkDataModel: EmployeeExperiences[] = [];
  employeeCurrentWorkDataModel: EmployeePosition[] = [];
  pedCategoryData: PedCategoryDto[] = [];

  /** Natijalar modal uchun yengil DTO — NSwag generatsiya qilingan client orqali. */
  getJobApplicationResult(jobApplicationId: number): Observable<JobApplicationResultDto>
  {
    return this.jobApplicationResultClient.getResult(jobApplicationId);
  }

  getApplicant(userService: UserService): Observable<ApplicantDetailsDto>
  {
    return userService.user$.pipe(
      take(1),
      switchMap(user =>
        this.applicantClient.getApplicant(user.pinfl).pipe(
          tap(value =>
          {
            this.applicant = value;
            this.applicant$.next(value);
            if (value.base64PassportPhoto) {
              localStorage.setItem('base64', value.base64PassportPhoto);
              localStorage.removeItem('profilePhotoUrl');
            } else {
              localStorage.removeItem('base64');
              localStorage.removeItem('profilePhotoUrl');
            }
          })
        )
      )
    );
  }

  getProfile(): Observable<ApplicantProfileDto>
  {
    return this.applicantClient.profile().pipe(
      tap(profile =>
      {
        this.applicant = profile;
        this.applicant$.next(profile);
        if (profile.base64PassportPhoto) {
          localStorage.setItem('base64', profile.base64PassportPhoto);
          localStorage.removeItem('profilePhotoUrl');
        } else {
          localStorage.removeItem('base64');
          localStorage.removeItem('profilePhotoUrl');
        }
      })
    );
  }

  /** Builds ApplicantDetailsDto from ApplicantProfileDto for refreshAndUpdateApplicantDetails (e.g. updatePhoto). */
  buildApplicantDetailsForUpdatePhoto(profile: ApplicantProfileDto): ApplicantDetailsDto
  {
    const dto = new ApplicantDetailsDto();
    dto.id = profile.id;
    dto.fullName = new FullName();
    dto.fullName.surname = profile.fullName?.surname ?? '';
    dto.fullName.name = profile.fullName?.name ?? '';
    dto.fullName.patronymic = profile.fullName?.patronymic ?? '';
    dto.fullName.fio = profile.fullName?.fio ?? '';
    dto.passport = new PassportData();
    dto.passport.pinfl = profile.passport?.pinfl ?? '';
    dto.passport.passportSeria = profile.passport?.passportSeria ?? '';
    dto.passport.passportNumber = profile.passport?.passportNumber ?? '';
    dto.address = new Address();
    dto.address.streetHome = profile.address?.streetHome;
    dto.contact = new ContactData();
    dto.contact.phoneMain = profile.contact?.phoneMain;
    dto.contact.eMail = profile.contact?.eMail;
    dto.isFromGcp = false;
    return dto;
  }

  getMyApplications(): Observable<JobApplicationMyDto[]>
  {
    return this.jobApplicantClient.myApplications().pipe(
      take(1),
      catchError(error =>
      {
        console.error('Error fetching job applications:', error);
        return of([]);
      })
    );
  }

  getApplicantsDiplomaData(_pinfl: string)
  {
    let requestDiploma = new RequestDiploma();
    requestDiploma.pinfl = _pinfl;
    let _diplomaData: PersonDiplomApi[] = [];

    this.mipClient.getPersonDiplomData(requestDiploma).subscribe({
      next: diplomaData =>
      {

        Object.assign(this.applicantDiplomaInfo, diplomaData);
      },
      error: err0r =>
      {
        console.error('Diploma data was not found: ', err0r);
      },
    });
  }

  getApplicantsWorkHistory(_pin: string)
  {
    let laborApiRequest = new LaborApiRequest();
    laborApiRequest.pin = _pin;
    let _workData: EmployeeWorkDataModel[] = [];

    this.mipClient.getEmployeeHistoryInfo(laborApiRequest).subscribe({
      next: laborData =>
      {

        Object.assign(
          this.employeeWorkDataModel,
          laborData?.result?.data?.experiences
        );
      },
      error: err0r =>
      {
        console.error('Work data was not found: ', err0r);
      },
    });
  }

  getApplicantsWorkCurrent(_pin: string)
  {
    let laborApiRequest = new LaborApiRequest();
    laborApiRequest.pin = _pin;
    let _workData: EmployeeCurrentWorkDataModel[] = [];

    this.mipClient.getEmployeeCurrentInfo(laborApiRequest).subscribe({
      next: laborData =>
      {

        Object.assign(
          this.employeeCurrentWorkDataModel,
          laborData?.result?.positions
        );
      },
      error: err0r =>
      {
        console.error('Work data was not found: ', err0r);
      },
    });
  }

  getApplicantsPedCategory(
    _pin: string,
    passportSeries: string,
    passportNum: string
  )
  {
    let categoryApiRequest = new PedCategoryRequestAPI();
    categoryApiRequest.pinfl = _pin;
    categoryApiRequest.passportSeries = passportSeries;
    categoryApiRequest.passportNumber = passportNum;

    let _categoryData: PedCategoryDto[] = [];

    this.mipClient
      .getPedCategoriesByPassportAndPINFL(categoryApiRequest)
      .subscribe({
        next: categoryData =>
        {

          Object.assign(this.pedCategoryData, categoryData);
        },
        error: err0r =>
        {
          console.error('category data was not found: ', err0r);
        },
      });
  }

  /** Updates applicant address, phone and email via PATCH Update; returns refreshed profile. */
  updateContactAndAddress(
    profile: ApplicantProfileDto,
    streetHome: string | undefined,
    phoneMain: string | undefined,
    eMail: string | undefined
  ): Observable<ApplicantProfileDto | null>
  {
    const request = new ApplicantProfileDto();
    request.id = profile.id;
    request.address = new ProfileAddressDto();
    request.address.streetHome = streetHome ?? profile.address?.streetHome;
    request.contact = new ProfileContactDto();
    request.contact.phoneMain = phoneMain ?? profile.contact?.phoneMain;
    request.contact.eMail = eMail ?? profile.contact?.eMail;
    return this.applicantClient.update(request).pipe(
      switchMap(() => this.getProfile()),
      tap(updated =>
      {
        if (updated) this.primeToast('success', 'data_has_been_updated');
      }),
      catchError(err =>
      {
        this.primeToast('error', 'data_could_not_be_updated');
        console.error('Error updating contact/address:', err);
        return of(null);
      })
    );
  }

  refreshAndGetApplicantInfo(mip: MIPIntegration): Observable<ApplicantDetailsDto | null>
  {
    const dto = this.buildApplicantDetailsForUpdatePhoto(this.applicant as ApplicantProfileDto);
    return this.applicantClient.refreshAndUpdateApplicantDetails(dto, mip).pipe(
      tap(updatedApplicant =>
      {
        if (updatedApplicant)
        {
          this.primeToast('success', 'data_has_been_updated');
        }
      }),
      catchError(err =>
      {
        this.primeToast('error', 'data_could_not_be_updated');
        console.error('Error updating applicant data:', err);
        return of(null);
      })
    );
  }

  getApplicationListByApplicantId(
    applicantId: number
  ): Observable<JobApplicationDto[]>
  {
    return this.jobApplicantClient
      .getJobApplicationsByApplicantId(applicantId)
      .pipe(
        map(applications =>
        {
          this.applications = applications;
          return applications;
        }),
        take(1),
        catchError(error =>
        {
          console.error('Error fetching job applications:', error);
          return of([]); // Return an empty array or handle the error differently
        })
      );
  }

  formatDate(date?: Date)
  {
    return this.pipe.transform(date, 'dd.MM.yyyy');
  }

  showFileUploadDialog()
  {
    this.ref = this.dialogService.open(DocumentsComponent, {
      header: this.translocoService.translate('applicant.upload_your_docs'),
      style:{
        'min-width':'50rem',
        'min-height':'20rem',
        'max-width':'80rem',
      },
      resizable: true,
    });
    return this.ref;
  }

  /** Profil (MyDocuments) orqali yuklanadigan fayl – ApplicantFile ga ketadi. */
  uploadApplicantFile(file: File[], spFileTypeId: number): Observable<any>
  {
    const formData = new FormData();

    const url =
      this._baseUrl + '/api/PiimaJob/JobApplication/UploadApplicantFile';

    formData.append('file', file[0], file[0].name);
    formData.append('applicantId', this.applicant.id.toString());
    formData.append('spFileTypeId', spFileTypeId.toString());

    return this._httpClient.post(url, formData);
  }

  /** Ariza bo'yicha fayllar ro'yxati (jobApplicationId orqali). */
  getJobApplicationFiles(jobApplicationId: number): Observable<JobApplicationFileDto[]>
  {
    return this.jobApplicationFilesClient.getJobApplicationFiles(jobApplicationId).pipe(
      catchError(() => of([]))
    );
  }

  /** Ariza faylini o'chirish (faqat Draft holatida). */
  deleteJobApplicationFile(fileId: string): Observable<boolean>
  {
    return this.jobApplicationFilesClient.deleteJobApplicationFile(fileId).pipe(
      catchError(() => of(false))
    );
  }

  /** Ariza faylini yuklab olish (brauzerda download). */
  downloadJobApplicationFile(fileId: string, fileName?: string): void
  {
    this.jobApplicationFilesClient.downloadJobApplicationFile(fileId).subscribe({
      next: (res) =>
      {
        if (res?.data)
        {
          const url = URL.createObjectURL(res.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName || res.fileName || 'file';
          a.click();
          URL.revokeObjectURL(url);
        }
      },
      error: () => this.primeToast('error', 'file_download_failed'),
    });
  }

  downloadFinalStatusFile(applicationId: number): void
  {
    this.jobApplicantClient.downloadFinalStatusFile(applicationId).subscribe({
      next: (res) =>
      {
        if (res?.data)
        {
          const url = URL.createObjectURL(res.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = res.fileName || 'final-status-file';
          a.click();
          URL.revokeObjectURL(url);
        }
      },
      error: () => this.primeToast('error', 'file_download_failed'),
    });
  }

  /** Ariza faylini yuklash (spFileTypeId — SP Applicant File Type, vacancyId — vakansiya id). */
  uploadJobApplicationFile(file: File, spFileTypeId: number, vacancyId: number): Observable<boolean>
  {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('spFileTypeId', String(spFileTypeId));
    formData.append('vacancyId', String(vacancyId));
    const url = this._baseUrl + '/api/PiimaJob/JobApplicationFiles/UploadJobApplicationFile';
    return this._httpClient.post<boolean>(url, formData).pipe(
      catchError(() => of(false))
    );
  }

  getVacancyById(vacancyId: number): Observable<VacancyDetailsDto>
  {
    return this.vacancyClient.getVacancyById(vacancyId);
  }

  primeToast(
    severity: 'success' | 'error' | 'info',
    summaryKey: string,
    details: string = undefined
  )
  {
    this.messageService.add({
      severity: severity,
      summary: this.translocoService.translate(summaryKey),
      detail: details,
      life: 5000,
    });
  }
}
